import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { withStyles } from '@material-ui/core/styles';
import Paper from '@material-ui/core/Paper';
import Typography from '@material-ui/core/Typography';
import Grid from '@material-ui/core/Grid';
import FormControl from '@material-ui/core/FormControl';
import InputLabel from '@material-ui/core/InputLabel';
import Select from '@material-ui/core/Select';
import MenuItem from '@material-ui/core/MenuItem';
import CircularProgress from '@material-ui/core/CircularProgress';

import api from '../../lib/api';
import AdminPageShell from '../../components/admin/AdminPageShell';
import UnifiedHallSchedule from '../../components/UnifiedHallSchedule';
import Link from '../../components/Link';
import { fetchCenters } from '../../ducks/centers';
import { fetchPlaygrounds } from '../../ducks/playgrounds';
import {
  todayRange,
  lastDaysRange,
  buildOperatorHomeCards,
} from '../../lib/operatorHomeStats';

function centerIdOf(center) {
  if (!center) return '';
  const id = center.id != null ? center.id : center.Id;
  return id != null && id !== '' ? String(id) : '';
}

function centerNameOf(center) {
  if (!center) return 'Без названия';
  return center.name || center.Name || 'Без названия';
}

function pickDefaultCenterId(centers, playgrounds) {
  const list = centers || [];
  const halls = playgrounds || [];
  const withHalls = list.find((c) => {
    const id = Number(centerIdOf(c));
    return halls.some((p) => Number(p.complexId || p.ComplexId) === id);
  });
  return centerIdOf(withHalls || list[0]);
}

export class OperatorHome extends Component {
  static propTypes = {
    centers: PropTypes.array,
    playgrounds: PropTypes.array,
    fetchCenters: PropTypes.func,
    fetchPlaygrounds: PropTypes.func,
    history: PropTypes.object,
    classes: PropTypes.object.isRequired,
  };

  static defaultProps = {
    centers: [],
    playgrounds: [],
    fetchCenters: null,
    fetchPlaygrounds: null,
    history: null,
  };

  constructor(props) {
    super(props);
    this.state = {
      centerId: pickDefaultCenterId(props.centers, props.playgrounds),
      playgroundId: '',
      todaySummary: null,
      weekSummary: null,
      loading: false,
      errorMessage: '',
    };
    this.summaryLoadSeq = 0;
  }

  safeFetch = (fn) => {
    if (!fn) return;
    try {
      const result = fn();
      if (result && typeof result.catch === 'function') result.catch(() => {});
    } catch (err) {
      // redux-promise / sync action
    }
  };

  componentDidMount() {
    if ((!this.props.centers || !this.props.centers.length)) {
      this.safeFetch(this.props.fetchCenters);
    }
    if ((!this.props.playgrounds || !this.props.playgrounds.length)) {
      this.safeFetch(this.props.fetchPlaygrounds);
    }
    if (this.state.centerId) {
      this.loadSummaries(this.state.centerId);
    }
  }

  componentDidUpdate(prevProps, prevState) {
    const prevCentersLen = (prevProps.centers || []).length;
    const centersLen = (this.props.centers || []).length;
    if (prevCentersLen === 0 && centersLen > 0 && !this.state.centerId) {
      const firstId = pickDefaultCenterId(this.props.centers, this.props.playgrounds);
      if (firstId) {
        this.setState({ centerId: firstId, playgroundId: '' }, () => {
          this.loadSummaries(firstId);
        });
      }
      return;
    }

    const prevPlaygroundsLen = (prevProps.playgrounds || []).length;
    const playgroundsLen = (this.props.playgrounds || []).length;
    if (prevPlaygroundsLen === 0 && playgroundsLen > 0 && this.state.centerId) {
      const preferred = pickDefaultCenterId(this.props.centers, this.props.playgrounds);
      const currentHalls = this.centerPlaygrounds(this.state.centerId);
      if (!currentHalls.length && preferred && preferred !== this.state.centerId) {
        this.setState({ centerId: preferred, playgroundId: '' }, () => {
          this.loadSummaries(preferred);
        });
        return;
      }
      this.syncPlaygroundForCenter(this.state.centerId);
    }

    if (prevState.centerId !== this.state.centerId && this.state.centerId) {
      this.syncPlaygroundForCenter(this.state.centerId);
    }
  }

  centerPlaygrounds = (centerId) => {
    const id = Number(centerId);
    return (this.props.playgrounds || []).filter(
      (p) => Number(p.complexId || p.ComplexId) === id,
    );
  };

  syncPlaygroundForCenter = (centerId) => {
    const halls = this.centerPlaygrounds(centerId);
    const current = this.state.playgroundId;
    const stillThere = halls.some((p) => String(p.id != null ? p.id : p.Id) === String(current));
    if (stillThere) return;
    const first = halls[0];
    const nextId = first ? String(first.id != null ? first.id : first.Id) : '';
    if (nextId !== current) this.setState({ playgroundId: nextId });
  };

  loadSummaries = async (centerId) => {
    if (!centerId) {
      this.setState({ loading: false, errorMessage: '', todaySummary: null, weekSummary: null });
      return;
    }
    const today = todayRange();
    const week = lastDaysRange(7);
    const seq = this.summaryLoadSeq + 1;
    this.summaryLoadSeq = seq;
    this.setState({ loading: true, errorMessage: '' });
    try {
      const scope = { complexId: centerId };
      const [todaySummary, weekSummary] = await Promise.all([
        api.getReportsSummary(scope, today.from, today.to),
        api.getReportsSummary(scope, week.from, week.to),
      ]);
      if (seq !== this.summaryLoadSeq) return;
      this.setState({
        todaySummary: todaySummary || null,
        weekSummary: weekSummary || null,
        loading: false,
      });
    } catch (error) {
      if (seq !== this.summaryLoadSeq) return;
      this.setState({
        loading: false,
        todaySummary: null,
        weekSummary: null,
        errorMessage: (error && error.message) || 'Не удалось загрузить отчёт',
      });
    }
  };

  handleCenterChange = (event) => {
    const nextId = event.target.value;
    if (!nextId) return;
    this.setState({ centerId: String(nextId), playgroundId: '' }, () => {
      this.loadSummaries(String(nextId));
    });
  };

  selectedCenter = () => {
    const { centerId } = this.state;
    return (this.props.centers || []).find((c) => centerIdOf(c) === String(centerId)) || null;
  };

  renderStats = () => {
    const { classes } = this.props;
    const { centerId, todaySummary, weekSummary, loading, errorMessage } = this.state;

    if (!centerId) {
      return (
        <Typography className={classes.hint}>Нет данных для отображения</Typography>
      );
    }
    if (loading) {
      return (
        <div className={classes.loading} data-testid="operator-home-stats-loading">
          <CircularProgress size={28} />
        </div>
      );
    }
    if (errorMessage) {
      return (
        <Typography color="error" className={classes.errorText}>{errorMessage}</Typography>
      );
    }
    if (!todaySummary && !weekSummary) {
      return (
        <Typography className={classes.hint}>Нет данных для отображения</Typography>
      );
    }

    const cards = buildOperatorHomeCards({
      todaySummary,
      weekSummary,
      centerId,
      today: todayRange(),
      week: lastDaysRange(7),
    });

    return (
      <Grid container spacing={2} className={classes.statsRow} data-testid="operator-home-stats">
        {cards.map((card) => (
          <Grid item xs={12} sm={6} md={3} key={card.key}>
            <Link to={card.to} className={classes.statCardLink}>
              <Paper className={classes.statCard} elevation={0}>
                <Typography variant="body2" color="textSecondary">{card.label}</Typography>
                <Typography variant="caption" color="textSecondary" className={classes.statPeriod}>
                  {card.period}
                </Typography>
                <Typography variant="h6" className={classes.statValue}>{card.value}</Typography>
              </Paper>
            </Link>
          </Grid>
        ))}
      </Grid>
    );
  };

  render() {
    const { classes, centers, history } = this.props;
    const { centerId, playgroundId } = this.state;
    const selected = this.selectedCenter();
    const pageTitle = selected ? centerNameOf(selected) : 'Клуб (спорткомплекс)';
    const halls = this.centerPlaygrounds(centerId);
    const hasCenters = (centers || []).length > 0;

    return (
      <AdminPageShell
        title={pageTitle}
        data-testid="operator-home"
        actions={hasCenters ? (
          <FormControl className={classes.centerSelect} margin="dense">
            <InputLabel>Клуб (спорткомплекс)</InputLabel>
            <Select
              value={centerId}
              onChange={this.handleCenterChange}
            >
              {(centers || []).map((c) => {
                const id = centerIdOf(c);
                if (!id) return null;
                return (
                  <MenuItem key={id} value={id}>
                    {centerNameOf(c)}
                  </MenuItem>
                );
              })}
            </Select>
          </FormControl>
        ) : null}
      >
        {this.renderStats()}
        {centerId ? (
          <UnifiedHallSchedule
            playgrounds={halls}
            selectedPlaygroundId={playgroundId}
            onPlaygroundChange={(id) => this.setState({ playgroundId: String(id) })}
            sportComplexId={centerId}
            history={history}
          />
        ) : null}
      </AdminPageShell>
    );
  }
}

const styles = (theme) => ({
  centerSelect: {
    minWidth: 280,
  },
  statsRow: {
    marginBottom: theme.spacing(2),
  },
  statCardLink: {
    display: 'block',
    height: '100%',
    textDecoration: 'none',
    color: 'inherit',
    '&:hover': {
      textDecoration: 'none',
      color: 'inherit',
    },
    '&:hover $statCard': {
      borderColor: theme.palette.primary.main,
      boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
    },
  },
  statCard: {
    padding: theme.spacing(2.5),
    borderRadius: 10,
    backgroundColor: '#ffffff',
    border: '1px solid #e9ecef',
    height: '100%',
    boxSizing: 'border-box',
  },
  statPeriod: {
    display: 'block',
    marginTop: 2,
  },
  statValue: {
    fontWeight: 600,
    marginTop: 8,
  },
  loading: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    padding: theme.spacing(3),
    minHeight: 88,
    marginBottom: theme.spacing(2),
  },
  errorText: {
    marginBottom: theme.spacing(2),
  },
  hint: {
    marginBottom: theme.spacing(2),
  },
});

export default connect(
  (state) => ({
    centers: state.centers || [],
    playgrounds: state.playgrounds || [],
  }),
  { fetchCenters, fetchPlaygrounds },
)(withStyles(styles)(OperatorHome));
